import React from "react";
import { Shape } from "../../../../../types";
import { TooltipProvider } from "../../../../ui/tooltip";
import { getAbsolutePositionOfShape } from "../../../helpers/getAbsolutePositionOfShape";
import { getWindowKonvaObject } from "../../../helpers/konva/getWindowKonvaObject";
import { useShapeMetrics } from "../../../helpers/shapeMetrics";
import { ShapeControlColor } from "./controls/ShapeControlColor";
import { ShapeControlDelete } from "./controls/ShapeControlDelete";
import {
  ShapeControlFlipHorizontal,
  ShapeControlFlipVertical
} from "./controls/ShapeControlFlip";
import { ShapeControlHide } from "./controls/ShapeControlHide";
import { ShapeControlLayerOrder } from "./controls/ShapeControlLayerOrder";
import { ShapeControlLock } from "./controls/ShapeControlLock";
import { ShapeToolboxSpotItems } from "./shape-types/ShapeToolboxSpotItems";
import { ShapeToolboxTextItems } from "./shape-types/ShapeToolboxTextItems";

interface ShapeToolboxProps {
  shape: Shape;
  stageScale: number;
  onUpdate: (id: string, newProps: Partial<Shape>) => void;
  onDelete: () => void;
  onLocate: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

export const ShapeToolbox: React.FC<ShapeToolboxProps> = ({
  shape,
  onUpdate,
  onDelete
}) => {
  const metrics = useShapeMetrics();
  if (!shape) return null;
  const absPos = metrics
    ? (() => {
        const rect = metrics.getScreenRect(shape.id);
        return rect ? { x: (rect.left + rect.right) / 2, y: rect.top } : null;
      })()
    : (() => {
        const stage = getWindowKonvaObject().stages[0];
        return stage ? getAbsolutePositionOfShape(shape.id, stage) : null;
      })();
  if (!absPos) return null;

  absPos.y = absPos.y - 80;

  const handleUpdate = (newProps: Partial<Shape>) => {
    onUpdate(shape.id, newProps);
  };

  return (
    <TooltipProvider>
      <div
        className="shape-tool-box -translate-x-1/2 -translate-y-full fade-in-0 zoom-in-95 fixed z-sticky flex animate-in items-center gap-0.5 rounded-lg border border-neutral-200 bg-background-primary p-1 shadow-lg"
        style={{
          left: `${absPos.x}px`,
          top: `${absPos.y}px`
        }}
      >
        <ShapeControlHide shape={shape} onUpdate={handleUpdate} />
        <ShapeControlLock shape={shape} onUpdate={handleUpdate} />
        <div className="h-4 w-px bg-neutral-200" />
        <ShapeControlColor shape={shape} onUpdate={handleUpdate} />

        {shape.type === "spot" && <ShapeToolboxSpotItems shape={shape} />}
        {shape.type === "text" && <ShapeToolboxTextItems shape={shape} />}

        {shape.type !== "spot" && (
          <>
            <div className="h-4 w-px bg-neutral-200" />
            <ShapeControlFlipHorizontal shape={shape} onUpdate={handleUpdate} />
            <ShapeControlFlipVertical shape={shape} onUpdate={handleUpdate} />
          </>
        )}
        <div className="h-4 w-px bg-neutral-200" />
        <ShapeControlLayerOrder shape={shape} onUpdate={handleUpdate} />
        <div className="h-4 w-px bg-neutral-200" />
        <ShapeControlDelete shape={shape} onDelete={onDelete} />
      </div>
    </TooltipProvider>
  );
};
